<template>
  <div class="organisation">
    <CommonHeader>
      <div slot="submenu-center" class="filters-container">
        <b-button pill size="sm" variant="primary" class="text-uppercase pl-3 mr-2" :to="{ name: 'AddCompany' }">
          <b-icon-plus class="mr-2" font-scale="2" />
          <span>{{ $t('Add Company') }}</span>
        </b-button>

        <b-button
          v-if="hasPermissionsToDelete && hasSelectedPersons"
          pill
          size="sm"
          variant="secondary"
          class="text-uppercase mx-2"
          @click="onDeletePersonsClick"
        >
          <span>{{ $t('Remove') }}</span>
        </b-button>

        <b-button
          v-if="hasSelectedPersons"
          pill
          :disabled="!hasExactlyTwoSelectedPersons"
          size="sm"
          variant="secondary"
          class="text-uppercase mx-2"
          @click="onMergePersonsClick"
        >
          <span>{{ $t('Merge') }}</span>
        </b-button>

        <IsAdmin>
          <b-button
            pill
            size="sm"
            variant="secondary"
            class="text-uppercase btn-text mx-2"
            @click="exportOrganisations"
          >
            <span>{{ $t('Export') }}</span>
          </b-button>
        </IsAdmin>

        <div class="flex-grow-1" />

        <filter-field
          show-list-on-focus
          :label="$t('Filter')"
          :items="compositeFilterItems"
          :filter-callback="compositeFilterSearch"
          :text-callback="compositeFilterExtractor"
          @update:selected="onCompositeFilterUpdate"
        />

        <filter-field
          class="ml-3"
          :label="$t('Free text search')"
          :value="filter.textRaw"
          @input="onTextFilterUpdate"
        />
      </div>
    </CommonHeader>

    <div v-if="hasSimplePermissions('fetchPersons')" style="display: flex">
      {{ /** @todo Get rid of this sidebar */ }}
      <div
        v-if="showSideBar"
        class="sidebar"
        :style="[isAgreement && innerHeight < 970 ? { 'overflow-y': 'scroll' } : '']"
      >
        <div class="agreement-section" :class="{ open: isAgreement }">
          <div class="agreement">
            <img class="top-img" src="static/arabica-6.png" />
            <img v-if="isAgreement" class="close-img" src="static/close.png" @click="closeAgreement()" />
            <h4 v-if="!isAgreement">{{ $t('UE2021, SKV4820, LOA2?') }}</h4>
            <h4 v-if="isAgreement">{{ $t('What regulations complies when hiring a contractor?') }}</h4>
            <div class="text" v-if="!isAgreement" style="margin-bottom: 5px">
              <p>
                {{ $t('Read more about what you need to know before making contract') }}
                <span @click="showAgreement()"><img class="arrows-img" src="static/arrows/right.png" /></span>
              </p>
            </div>
            <div v-if="isAgreement">
              <div class="text">
                <p>
                  {{
                    $t(
                      'What and if the company needs a collective agreement is regulated by the occupation, you will find info via the link below, there you will also find if we used them before and therefore approved, in that case jump to step 3.'
                    )
                  }}
                </p>
              </div>
              <div class="text-link">
                <p>
                  <a target="_blank" href="https://www.dropbox.com/s/m9muk6i73mn8g0e/H2_UE%202015-Register.xlsx?dl=0">
                    {{ $t('- Regulation list based on occupation') }}</a
                  >
                </p>
              </div>
              <div class="text">
                <p>{{ $t('2. If needed agreement, first search via their homepage below:') }}</p>
              </div>
              <div class="text-link">
                <p>
                  <a target="_blank" href="https://byggforetagen.se/om-oss/medlemsforetag/">{{
                    $t('- Search connected companies ”BF” here')
                  }}</a>
                </p>
                <p>
                  <a target="_blank" href="https://www.me.se/om-oss/sok-medlemmar/">{{
                    $t('- Search connected companies ”ME” here')
                  }}</a>
                </p>
              </div>
              <div class="text">
                <p>
                  {{
                    $t(
                      '3. If all above is approved make the contractor send in all according to the checklist, you will find this and all needed documents via link below:'
                    )
                  }}
                </p>
              </div>
              <div class="text-link">
                <p>
                  <a
                    target="_blank"
                    href="https://www.dropbox.com/work/H2%20Entreprenad%20Team%20Folder/H2%20Mallar/01.%20Projektering/03.%20Anm%C3%A4lan%20UE"
                    >{{ $t('- Checklist + Documentation') }}</a
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <person-list
        :selection="selection"
        :show-side-bar="showSideBar"
        :filter-text="filter.text"
        :filter-city="filter.city"
        :filter-occupation="filter.occupation"
        :with-missing-ue-file="filter.withMissingUeFile"
        :with-code-of-conduct-status="filter.withCodeOfConductStatus"
        @update:selection="onSelectionUpdate"
        @update:items="onItemsUpdate"
      />

      <merge-persons-dialog
        :visible="mergePersons.show"
        :left-person-id="mergePersons.leftPersonId"
        :right-person-id="mergePersons.rightPersonId"
        @close="onMergePersonsClose"
        @save="onMergePersonsSave"
      />

      <delete-persons-dialog
        :visible="deletePersons.show"
        :person-ids="deletePersons.personIds"
        @cancel="onDeletePersonsCancel"
        @confirm="onDeletePersonsConfirm"
      />

      <b-modal
        v-if="$route.name !== 'OrganisationOverview'"
        centered
        hide-header
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        scrollable
        visible
        dialog-class="modal-no-padding modal-color-black entity-edit-form"
      >
        <router-view :filterText="filter.text" />
      </b-modal>
    </div>

    <CommonFooter />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import At from 'vue-at/dist/vue-at';
import debounce from 'debounce';
import sanitizeHtml from 'sanitize-html';
import { saveAs } from 'file-saver';

import constants from '@/constants';

import CommonHeader from '@/components/toolbar/CommonHeader';
import CommonFooter from '@/components/toolbar/CommonFooter';
import FilterField from '@/components/organisation/FilterField';
import IsAdmin from '@/components/toolbar/IsAdmin';
import MergePersonsDialog from '@/components/organisation/MergePersonsDialog';
import DeletePersonsDialog from '@/components/organisation/DeletePersonsDialog';
import PersonList from '@/components/organisation/PersonList';
import getSearchRegExp from '@/utils/getSearchRegExp';

export default {
  name: 'OrganisationOverview',

  components: {
    At,
    CommonHeader,
    CommonFooter,
    FilterField,
    IsAdmin,
    MergePersonsDialog,
    DeletePersonsDialog,
    PersonList,
  },

  props: {
    showSideBar: {
      type: Boolean,
      default: false,
    },
  },

  metaInfo() {
    return {
      title: this.$t('Organisation'),
      titleTemplate: 'Boon - %s',
    };
  },

  data() {
    const defaultFilter = {
      text: '',
      // This is actually unused.
      textRaw: '',
      city: '',
      occupation: '',
      withMissingUeFile: false,
      withCodeOfConductStatus: null,
    };
    return {
      items: [],
      defaultFilter,
      filter: { ...defaultFilter },
      selection: [],
      mergePersons: {
        show: false,
        /** @todo It doesn't work for some weird reason */
        // An easy way to make sure that form rerenders when loading with new params.
        key: Math.random(),
        leftPersonId: null,
        rightPersonId: null,
      },
      deletePersons: {
        show: false,
        personIds: [],
      },
      isAgreement: false,
      innerHeight: 0,
    };
  },

  computed: {
    ...mapGetters(['hasSimplePermissions', 'exportOrganizationToXlsx', 'cities', 'occupations']),

    compositeFilterItems() {
      const items = this.cities.map((city) => ({
        type: 'city',
        text: city,
      }));

      this.occupations.forEach((occupation) => {
        items.push({
          type: 'occupation',
          text: occupation,
        });
      });

      items.push({
        type: 'missingUefile',
        text: this.$t('Missing UE-2021 file'),
      });

      Object.values(constants.companyFiles.codeOfConductStatuses).forEach((status) => {
        items.push({
          type: 'codeOfConduct',
          text: this.$t(`codeOfConduct.filter.${status}`),
          value: status,
        });
      });

      return items.sort((a, b) => a.text.localeCompare(b.text));
    },

    hasSelectedPersons() {
      return !!this.selection.length;
    },

    hasExactlyTwoSelectedPersons() {
      let count = 0;
      this.selection.some((entry) => {
        count += entry.items.length;
        return count > 2;
      });

      return count === 2;
    },

    hasPermissionsToDelete() {
      return this.hasSimplePermissions('deletePerson');
    },
  },

  mounted() {
    this.innerHeight = window.innerHeight;
  },

  methods: {
    ...mapActions(['deletePerson']),

    exportOrganisations(event) {
      event.preventDefault();
      const xlsx = this.exportOrganizationToXlsx(this.items);
      const blob = new Blob([xlsx], { type: 'application/octet-stream' });
      saveAs(blob, 'Organisation.xlsx');
    },

    onTextFilterUpdate: debounce(function (text) {
      /** @todo I've no idea why we need to sanitize this text. */
      this.filter.text = text ? this.sanitize(text) : '';
    }, 400),

    sanitize(text) {
      return sanitizeHtml(text, {
        allowedTags: ['span'],
        allowedAttributes: {
          span: ['data-id', 'contenteditable'],
        },
        allowedClasses: {
          span: ['tag'],
        },
      });
    },

    compositeFilterExtractor(item) {
      return item ? item.text : '';
    },

    compositeFilterSearch(items, search) {
      if (!search) {
        return items;
      }
      const regexp = getSearchRegExp(search);
      return items.filter((item) => regexp.test(item.text));
    },

    onCompositeFilterUpdate(item) {
      this.filter = { ...this.defaultFilter };
      if (!item) {
        return;
      }

      switch (item.type) {
        case 'city':
          this.filter.city = item.text;
          break;

        case 'occupation':
          this.filter.occupation = item.text;
          break;

        case 'missingUefile':
          this.filter.withMissingUeFile = true;
          break;

        case 'codeOfConduct':
          this.filter.withCodeOfConductStatus = item.value;
          break;
      }
    },

    onSelectionUpdate(selection) {
      this.selection = selection;
    },

    onMergePersonsClick() {
      this.mergePersons.show = true;
      this.mergePersons.key = Math.random();
      this.selection.forEach((entry) => {
        return entry.items.forEach((item) => {
          if (!this.mergePersons.leftPersonId) {
            this.mergePersons.leftPersonId = item;
          } else if (!this.mergePersons.rightPersonId) {
            this.mergePersons.rightPersonId = item;
          }
        });
      });
    },

    onMergePersonsSave() {
      this.selection = [];
      this.onMergePersonsClose();
    },

    onMergePersonsClose() {
      this.mergePersons = {
        show: false,
        leftPersonId: null,
        rightPersonId: null,
      };
    },

    onDeletePersonsClick() {
      if (!this.hasPermissionsToDelete) {
        return;
      }

      const personIds = [];
      this.selection.forEach((entry) => {
        entry.items.forEach((personId) => personIds.push(personId));
      });

      this.deletePersons.show = !!personIds.length;
      this.deletePersons.personIds = personIds;
    },

    onDeletePersonsCancel() {
      this.deletePersons.show = false;
      this.deletePersons.personIds = [];
    },

    onDeletePersonsConfirm() {
      this.deletePersons.show = false;
      this.deletePersons.personIds = [];
      this.selection = [];
    },

    showAgreement() {
      if (!this.isAgreement) {
        this.isAgreement = true;
      }
    },

    closeAgreement() {
      this.isAgreement = false;
    },

    onItemsUpdate(items) {
      this.items = items;
    },
  },
};
</script>

<style scoped>
.organisation .filters-container {
  width: 1344px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: stretch;
}

.organisation .sidebar {
  background: #1c2731;
  width: 250px;
  padding-left: 10px;
  color: #fff;
  height: 100vh;
  position: fixed;
  /* Header's height is 80px; subheader's height is 3.375rem */
  padding-top: calc(80px + 3.375rem);
  display: flex;
  flex-direction: column;
  align-content: space-between;
  flex-flow: wrap;
}

.organisation .sidebar .agreement-section {
  margin-top: 58px;
  padding-bottom: 20px;
}

.organisation .sidebar .agreement-section.open {
  margin-top: 101px;
}

.sidebar .agreement-section .top-img {
  margin-top: -58px;
  margin-bottom: 5px;
}

.sidebar .agreement-section.open .top-img {
  margin-top: -101px;
}

.organisation .sidebar .agreement {
  background: #fff;
  border-radius: 25px;
  padding: 10px;
  margin-right: 10px;
  color: #000;
  position: relative;
}

.sidebar .agreement .close-img {
  position: absolute;
  width: 40px;
  right: 10px;
  top: 5px;
  cursor: pointer;
}

.sidebar .agreement .arrows-img {
  width: 20px;
  cursor: pointer;
}

.sidebar .agreement h4 {
  font-size: 15px;
  margin-bottom: 10px;
  color: #000;
}

.sidebar .agreement .text {
  font-size: 13px;
  margin: 0;
  margin-bottom: 2px;
}

.sidebar .agreement .text p {
  font-family: inherit;
}

.sidebar .agreement .text-link {
  margin-bottom: 20px;
}

.sidebar .agreement .text-link p {
  font-size: 12px;
  text-decoration: underline;
  font-weight: 400;
}

.sidebar .agreement .icon-link {
  color: #fff;
}

.sidebar .agreement .icon-link div {
  background: #011531;
  font-size: 11px;
  margin-bottom: 10px;
  border-radius: 25px;
  padding: 8px;
}
</style>

<style>
.modal .modal-dialog.entity-edit-form {
  min-width: 1344px;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/OrganisationOverview.vue